import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ShieldAlert, Lock, Ban, UserPlus, RefreshCw, Activity, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_STYLES = {
  user_registered: 'bg-amber-50 text-amber-700',
  user_created: 'bg-blue-50 text-blue-700',
  user_login: 'bg-emerald-50 text-emerald-700',
  user_logout: 'bg-slate-100 text-slate-600',
  user_updated: 'bg-purple-50 text-purple-700',
  user_deactivated: 'bg-red-50 text-red-700',
  user_activated: 'bg-green-50 text-green-700',
};

const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

const KpiCard = ({ icon: Icon, label, value, accent, testid, alert }) => (
  <Card data-testid={testid} className={alert ? 'border-red-300' : ''}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900" data-testid={`${testid}-value`}>{value}</p>
        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const SecurityCenter = () => {
  const { user, getAuthHeaders } = useAuth();
  const [overview, setOverview] = useState(null);
  const [lockouts, setLockouts] = useState([]);
  const [throttled, setThrottled] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.roles?.includes('SuperAdmin');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [o, l, t, e] = await Promise.all([
        axios.get(`${API_URL}/api/security/overview`, { headers }),
        axios.get(`${API_URL}/api/security/lockouts`, { headers }),
        axios.get(`${API_URL}/api/security/throttled`, { headers }),
        axios.get(`${API_URL}/api/security/events?limit=150`, { headers }),
      ]);
      setOverview(o.data);
      setLockouts(l.data || []);
      setThrottled(t.data || []);
      setEvents(e.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // live refresh every 60s
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="security-center-denied">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Access Denied</h3>
            <p className="text-slate-500">Only SuperAdmin can access the Security Center.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="security-center-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-600" /> Security Center
          </h1>
          <p className="text-slate-600 mt-1">Live brute-force lockouts, throttled IPs, and account activity</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="security-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {overview?.registration_spike && (
        <div
          data-testid="registration-spike-banner"
          className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700"
        >
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm font-medium">
            Registration spike detected — {overview.registrations_1h} sign-ups in the last hour
            (threshold {overview.spike_threshold}). Review the registration events below.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-active-lockouts" icon={Lock} label="Active lockouts"
          value={overview?.active_lockouts ?? '—'} accent="bg-red-50 text-red-600"
          alert={(overview?.active_lockouts || 0) > 0} />
        <KpiCard testid="kpi-throttled-ips" icon={Ban} label="Throttled IPs"
          value={overview?.throttled_ips ?? '—'} accent="bg-orange-50 text-orange-600"
          alert={(overview?.throttled_ips || 0) > 0} />
        <KpiCard testid="kpi-registrations-24h" icon={UserPlus} label="Registrations (24h)"
          value={overview?.registrations_24h ?? '—'} accent="bg-amber-50 text-amber-600" />
        <KpiCard testid="kpi-failed-attempts" icon={ShieldAlert} label="Failed login attempts"
          value={overview?.total_failed_attempts ?? '—'} accent="bg-slate-100 text-slate-600" />
      </div>

      <Tabs defaultValue="lockouts" className="w-full">
        <TabsList>
          <TabsTrigger value="lockouts" data-testid="tab-lockouts">
            <Lock className="w-4 h-4 mr-2" /> Lockouts ({lockouts.length})
          </TabsTrigger>
          <TabsTrigger value="throttled" data-testid="tab-throttled">
            <Ban className="w-4 h-4 mr-2" /> Throttled IPs ({throttled.length})
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">
            <Activity className="w-4 h-4 mr-2" /> Security Audit ({events.length})
          </TabsTrigger>
        </TabsList>

        {/* Lockouts */}
        <TabsContent value="lockouts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Brute-Force Lockouts</CardTitle>
              <CardDescription>Tracked failed-login identifiers (source IP + account)</CardDescription>
            </CardHeader>
            <CardContent>
              {lockouts.length === 0 ? (
                <p className="text-center py-8 text-slate-500" data-testid="lockouts-empty">No tracked login failures 🎉</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="lockouts-table">
                    <thead className="text-left text-slate-500 border-b">
                      <tr>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2 pr-4">IP</th>
                        <th className="py-2 pr-4">Company</th>
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4">Fails</th>
                        <th className="py-2 pr-4">Last attempt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lockouts.map((r, i) => (
                        <tr key={i} data-testid={`lockout-row-${i}`} className="border-b last:border-0">
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${r.is_locked ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                              {r.is_locked ? 'Locked' : 'Tracking'}
                            </span>
                          </td>
                          <td className="py-2 pr-4 font-mono text-xs">{r.ip}</td>
                          <td className="py-2 pr-4">{r.company_code || '—'}</td>
                          <td className="py-2 pr-4">{r.email || '—'}</td>
                          <td className="py-2 pr-4 font-semibold">{r.fail_count}</td>
                          <td className="py-2 pr-4 text-slate-500">{fmt(r.last_failed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Throttled */}
        <TabsContent value="throttled" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Rate-Limited Sources</CardTitle>
              <CardDescription>IPs hitting the registration / abuse rate limits</CardDescription>
            </CardHeader>
            <CardContent>
              {throttled.length === 0 ? (
                <p className="text-center py-8 text-slate-500" data-testid="throttled-empty">No active rate-limit windows</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="throttled-table">
                    <thead className="text-left text-slate-500 border-b">
                      <tr>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2 pr-4">Scope</th>
                        <th className="py-2 pr-4">IP</th>
                        <th className="py-2 pr-4">Requests</th>
                        <th className="py-2 pr-4">Window start</th>
                      </tr>
                    </thead>
                    <tbody>
                      {throttled.map((r, i) => (
                        <tr key={i} data-testid={`throttled-row-${i}`} className="border-b last:border-0">
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${r.over_limit ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
                              {r.over_limit ? 'Throttled' : 'Counting'}
                            </span>
                          </td>
                          <td className="py-2 pr-4">{r.scope}</td>
                          <td className="py-2 pr-4 font-mono text-xs">{r.ip}</td>
                          <td className="py-2 pr-4 font-semibold">{r.count}</td>
                          <td className="py-2 pr-4 text-slate-500">{fmt(r.window_start)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Audit events */}
        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Audit Trail</CardTitle>
              <CardDescription>Account & role events: registrations, creations, logins, role grants</CardDescription>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-center py-8 text-slate-500" data-testid="events-empty">No security events recorded</p>
              ) : (
                <div className="space-y-2" data-testid="events-list">
                  {events.map((ev, i) => (
                    <div key={ev.id || i} data-testid={`event-row-${i}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_STYLES[ev.action] || 'bg-slate-100 text-slate-600'}`}>
                          {ev.action}
                        </span>
                        <div>
                          <p className="text-sm text-slate-800">
                            {ev.details?.email || ev.resource_id || ev.actor_id || '—'}
                            {ev.details?.roles ? (
                              <span className="ml-2 text-xs text-slate-500">
                                roles: {Array.isArray(ev.details.roles) ? ev.details.roles.join(', ') : String(ev.details.roles)}
                              </span>
                            ) : null}
                          </p>
                          <p className="text-xs text-slate-500">
                            {ev.company_code || ev.tenant_name || '—'}{ev.ip ? ` • ${ev.ip}` : ''}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">{fmt(ev.timestamp)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityCenter;
